import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trophy, Clock, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MemoryGamePage = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer
  useEffect(() => {
    let interval;
    if (startTime && !gameComplete) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameComplete]);

  const initGame = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/games/vocabulary-match`);
      const words = response.data.words;
      
      // Create pairs of cards (English and Portuguese)
      const cardPairs = [];
      words.forEach((word, index) => {
        cardPairs.push({ id: `en-${index}`, text: word.en, pairId: index, type: 'en' });
        cardPairs.push({ id: `pt-${index}`, text: word.pt, pairId: index, type: 'pt' });
      });
      
      // Shuffle cards
      const shuffled = cardPairs.sort(() => Math.random() - 0.5);
      setCards(shuffled);
    } catch (error) {
      // Fallback data
      const fallbackWords = [
        { en: 'Hello', pt: 'Olá' },
        { en: 'Thank you', pt: 'Obrigado' },
        { en: 'Please', pt: 'Por favor' },
        { en: 'Sorry', pt: 'Desculpe' },
        { en: 'Help', pt: 'Ajuda' },
        { en: 'Work', pt: 'Trabalho' },
        { en: 'Food', pt: 'Comida' },
        { en: 'Water', pt: 'Água' },
      ];
      
      const cardPairs = [];
      fallbackWords.forEach((word, index) => {
        cardPairs.push({ id: `en-${index}`, text: word.en, pairId: index, type: 'en' });
        cardPairs.push({ id: `pt-${index}`, text: word.pt, pairId: index, type: 'pt' });
      });
      
      setCards(cardPairs.sort(() => Math.random() - 0.5));
    } finally {
      setLoading(false);
      setFlipped([]);
      setMatched([]);
      setMoves(0);
      setGameComplete(false);
      setStartTime(Date.now());
      setElapsedTime(0);
    }
  }, []);

  useEffect(() => {
    initGame();
  }, [initGame]);

  const handleCardClick = (card) => {
    // Don't flip if already flipped or matched
    if (flipped.includes(card.id) || matched.includes(card.pairId) || flipped.length >= 2) {
      return;
    }

    const newFlipped = [...flipped, card.id];
    setFlipped(newFlipped);

    // Check for match when 2 cards are flipped
    if (newFlipped.length === 2) {
      setMoves(prev => prev + 1);
      
      const firstCard = cards.find(c => c.id === newFlipped[0]);
      const secondCard = cards.find(c => c.id === newFlipped[1]);

      if (firstCard.pairId === secondCard.pairId && firstCard.type !== secondCard.type) {
        // Match found!
        setMatched(prev => [...prev, firstCard.pairId]);
        setFlipped([]);
        
        // Check if game complete
        if (matched.length + 1 === cards.length / 2) {
          setGameComplete(true);
        }
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setFlipped([]);
        }, 1000);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateXP = () => {
    // Base XP + bonus for fewer moves and faster time
    const baseXP = 50;
    const moveBonus = Math.max(0, 30 - moves) * 2;
    const timeBonus = Math.max(0, 120 - elapsedTime);
    return baseXP + moveBonus + timeBonus;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando jogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold">Jogo da Memória</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={initGame}
          >
            <RotateCcw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          <div className="text-center">
            <div className="flex items-center gap-1 text-primary">
              <Clock className="w-4 h-4" />
              <span className="font-semibold">{formatTime(elapsedTime)}</span>
            </div>
            <p className="text-xs text-muted-foreground">Tempo</p>
          </div>
          <div className="text-center">
            <span className="font-semibold text-accent">{moves}</span>
            <p className="text-xs text-muted-foreground">Jogadas</p>
          </div>
          <div className="text-center">
            <span className="font-semibold text-success">{matched.length}/{cards.length / 2}</span>
            <p className="text-xs text-muted-foreground">Pares</p>
          </div>
        </div>
      </div>

      {/* Game Board */}
      <main className="flex-1 p-4">
        <div className="max-w-lg mx-auto">
          <div className="grid grid-cols-4 gap-2">
            {cards.map((card) => {
              const isFlipped = flipped.includes(card.id) || matched.includes(card.pairId);
              const isMatched = matched.includes(card.pairId);
              
              return (
                <button
                  key={card.id}
                  onClick={() => handleCardClick(card)}
                  disabled={isMatched}
                  className={cn(
                    'aspect-square rounded-xl text-sm font-medium transition-all duration-300 transform',
                    isFlipped
                      ? isMatched
                        ? 'bg-success/20 border-2 border-success text-success rotate-0'
                        : 'bg-primary text-primary-foreground rotate-0'
                      : 'bg-muted hover:bg-muted/80 text-transparent rotate-y-180'
                  )}
                  style={{
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {isFlipped ? (
                    <span className="block p-1 text-xs sm:text-sm">{card.text}</span>
                  ) : (
                    <span className="text-xl">?</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 rounded-xl bg-muted/50 text-center">
            <p className="text-sm text-muted-foreground">
              💡 Encontre os pares: palavra em inglês + tradução em português
            </p>
          </div>
        </div>
      </main>

      {/* Game Complete Modal */}
      {gameComplete && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="card-elevated p-6 max-w-sm w-full text-center space-y-4 animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <Trophy className="w-10 h-10 text-success" />
            </div>
            
            <div>
              <h2 className="font-display text-xl font-bold">Parabéns! 🎉</h2>
              <p className="text-muted-foreground">Você completou o jogo!</p>
            </div>

            <div className="grid grid-cols-3 gap-4 py-4">
              <div className="text-center">
                <p className="text-xl font-bold text-primary">{formatTime(elapsedTime)}</p>
                <p className="text-xs text-muted-foreground">Tempo</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-accent">{moves}</p>
                <p className="text-xs text-muted-foreground">Jogadas</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-success">+{calculateXP()}</p>
                <p className="text-xs text-muted-foreground">XP</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(-1)}
              >
                Voltar
              </Button>
              <Button
                className="flex-1 btn-primary-gradient"
                onClick={initGame}
              >
                Jogar Novamente
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryGamePage;
